import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuthStore } from '../stores/authStore';

function WelcomePage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    admin_name: '',
    institution_type: '',
    tax_id: '',
    admin_phone: '',
    church_name: '',
    expected_members: '',
  });
  
  const navigate = useNavigate();
  const { user, setTenantId } = useAuthStore();
  const totalSteps = 6;
  
  // Verifica se usuário já tem tenant ao carregar
  useEffect(() => {
    if (user?.tenant_id && currentStep !== 'success') {
      // Usuário já tem tenant, redireciona para dashboard
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate, currentStep]);
  
  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      setError('');
    } else {
      handleSubmit();
    }
  };
  
  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError('');
    }
  };
  
  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await api.post('/tenants', {
        church_name: formData.church_name,
        admin_phone: formData.admin_phone,
        institution_type: formData.institution_type || 'igreja',
        tax_id: formData.tax_id || null,
        expected_members: parseInt(formData.expected_members) || 0,
        address: null, // Pode ser adicionado depois
      });
      
      if (response.data.success) {
        const { tenant } = response.data.data;
        
        // Atualiza o tenant_id no authStore
        if (tenant?.id) {
          setTenantId(tenant.id);
          
          // Atualiza o usuário no store (com roles novas vindas do backend)
          useAuthStore.setState({
            user: {
              ...useAuthStore.getState().user,
              ...response.data.data.user, // Pega os dados completos incluindo roles
              tenant_id: tenant.id
            },
            tenantId: tenant.id,
            token: response.data.data.tokens?.access_token || useAuthStore.getState().token,
            refreshToken: response.data.data.tokens?.refresh_token || useAuthStore.getState().refreshToken
          });

          // Atualiza o header de autorização com o novo token
          if (response.data.data.tokens?.access_token) {
            api.defaults.headers.common['Authorization'] = `Bearer ${response.data.data.tokens.access_token}`;
          }
        }
        
        setCurrentStep('success');
      } else {
        setError(response.data.message || 'Erro ao criar conta');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error?.message || 'Erro ao criar conta. Tente novamente.';
      setError(errorMessage);
      console.error('Onboarding error:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const goToDashboard = () => {
    navigate('/dashboard', { replace: true });
  };
  
  const updateProgress = () => {
    const steps = document.querySelectorAll('.progress-step');
    steps.forEach((step, index) => {
      step.classList.remove('active', 'completed');
      if (index + 1 < currentStep) {
        step.classList.add('completed');
      } else if (index + 1 === currentStep) {
        step.classList.add('active');
      }
    });
  };
  
  React.useEffect(() => {
    updateProgress();
  }, [currentStep]);
  
  return (
    <div className="onboarding-container">
      {/* Header */}
      <header className="onboarding-header">
        <div className="onboarding-header-content">
          <div className="onboarding-logo">
            <div className="onboarding-logo-icon">H</div>
            <span className="onboarding-logo-text">Haklai</span>
          </div>
          <button className="btn btn-ghost btn-sm" type="button">
            <span className="material-symbols-outlined">help</span>
            Ajuda
          </button>
        </div>
      </header>

      {/* Progress */}
      <div className="progress-container">
        <div className="progress-content">
          <div className="progress-bar">
            {[...Array(totalSteps)].map((_, i) => (
              <div
                key={i + 1}
                className={`progress-step ${i + 1 < currentStep ? 'completed' : ''} ${i + 1 === currentStep ? 'active' : ''}`}
                data-step={i + 1}
              />
            ))}
          </div>
          <div className="progress-info">
            <span className="current-step-text">
              Passo {currentStep} de {totalSteps}
            </span>
            <span className="step-time">~2 minutos</span>
          </div>
        </div>
      </div>

      {/* Main */}
      <main className="onboarding-main">
        <div className="onboarding-card">
          {/* Step 1: Admin Name */}
          {currentStep === 1 && (
            <div className="onboarding-step active" data-step="1">
              <div className="onboarding-card-header">
                <h1>Vamos começar!</h1>
                <p>Primeiro, nos conte um pouco sobre você</p>
              </div>
              <div className="onboarding-card-body">
                <form className="onboarding-form">
                  {error && <div className="alert alert-error">{error}</div>}
                  <div className="form-group">
                    <label className="form-label">Qual é o seu nome completo?</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Ex: Pastor João Silva"
                      value={formData.admin_name}
                      onChange={(e) => updateFormData('admin_name', e.target.value)}
                      required
                    />
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Step 2: Institution Type */}
          {currentStep === 2 && (
            <div className="onboarding-step active" data-step="2">
              <div className="onboarding-card-header">
                <h1>Tipo de Instituição</h1>
                <p>Qual o tipo da sua instituição religiosa?</p>
              </div>
              <div className="onboarding-card-body">
                {error && <div className="alert alert-error">{error}</div>}
                <div className="option-cards">
                  {[
                    { value: 'igreja', icon: 'church', title: 'Igreja', desc: 'Igreja local ou denominação' },
                    { value: 'ministerio', icon: 'diversity_3', title: 'Ministério', desc: 'Ministério ou movimento' },
                    { value: 'missao', icon: 'public', title: 'Missão', desc: 'Organização missionária' },
                    { value: 'outro', icon: 'more_horiz', title: 'Outro', desc: 'Outro tipo de organização' },
                  ].map((option) => (
                    <div
                      key={option.value}
                      className={`option-card ${formData.institution_type === option.value ? 'selected' : ''}`}
                      onClick={() => updateFormData('institution_type', option.value)}
                    >
                      <div className="option-card-icon">
                        <span className="material-symbols-outlined">{option.icon}</span>
                      </div>
                      <div className="option-card-title">{option.title}</div>
                      <div className="option-card-desc">{option.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Tax ID */}
          {currentStep === 3 && (
            <div className="onboarding-step active" data-step="3">
              <div className="onboarding-card-header">
                <h1>Identificação Fiscal</h1>
                <p>Informe o NIF/VAT ou CNPJ da sua organização</p>
              </div>
              <div className="onboarding-card-body">
                <form className="onboarding-form">
                  {error && <div className="alert alert-error">{error}</div>}
                  <div className="form-group">
                    <label className="form-label">NIF/VAT ou CNPJ</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Ex: 12.345.678/0001-90"
                      value={formData.tax_id}
                      onChange={(e) => updateFormData('tax_id', e.target.value)}
                    />
                    <span className="form-hint">Este campo é opcional, mas ajuda na emissão de faturas</span>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Step 4: Contact */}
          {currentStep === 4 && (
            <div className="onboarding-step active" data-step="4">
              <div className="onboarding-card-header">
                <h1>Contato</h1>
                <p>Como podemos entrar em contato com você?</p>
              </div>
              <div className="onboarding-card-body">
                <form className="onboarding-form">
                  {error && <div className="alert alert-error">{error}</div>}
                  <div className="form-group">
                    <label className="form-label">Telefone / WhatsApp</label>
                    <input
                      type="tel"
                      className="form-input"
                      placeholder="Ex: +55 11 99999-9999"
                      value={formData.admin_phone}
                      onChange={(e) => updateFormData('admin_phone', e.target.value)}
                      required
                    />
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Step 5: Church Name */}
          {currentStep === 5 && (
            <div className="onboarding-step active" data-step="5">
              <div className="onboarding-card-header">
                <h1>Nome da Organização</h1>
                <p>Qual o nome da sua igreja ou ministério?</p>
              </div>
              <div className="onboarding-card-body">
                <form className="onboarding-form">
                  {error && <div className="alert alert-error">{error}</div>}
                  <div className="form-group">
                    <label className="form-label">Nome da Igreja ou Ministério</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Ex: Igreja Vida Nova"
                      value={formData.church_name}
                      onChange={(e) => updateFormData('church_name', e.target.value)}
                      required
                    />
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Step 6: Member Count */}
          {currentStep === 6 && (
            <div className="onboarding-step active" data-step="6">
              <div className="onboarding-card-header">
                <h1>Quantidade de Membros</h1>
                <p>Quantos membros serão geridos pelo Haklai?</p>
              </div>
              <div className="onboarding-card-body">
                <form className="onboarding-form">
                  {error && <div className="alert alert-error">{error}</div>}
                  <div className="form-group">
                    <label className="form-label">Número aproximado de membros</label>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="Ex: 150"
                      min="1"
                      value={formData.expected_members}
                      onChange={(e) => updateFormData('expected_members', e.target.value)}
                      required
                    />
                    <span className="form-hint">Inclua membros, frequentadores e visitantes regulares</span>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Success Screen */}
          {currentStep === 'success' && (
            <div className="onboarding-step active" data-step="success">
              <div className="success-screen">
                <div className="success-icon">
                  <span className="material-symbols-outlined">check_circle</span>
                </div>
                <h2>Tudo pronto!</h2>
                <p>Sua conta foi criada com sucesso. Agora você pode começar a gerenciar seu ministério.</p>
                <button className="btn btn-primary btn-lg" onClick={goToDashboard}>
                  Ir para o Dashboard
                  <span className="material-symbols-outlined">arrow_forward</span>
                </button>
              </div>
            </div>
          )}

          {/* Navigation */}
          {currentStep !== 'success' && (
            <div className="onboarding-nav">
              <button
                className="btn btn-secondary"
                onClick={prevStep}
                style={{ visibility: currentStep === 1 ? 'hidden' : 'visible' }}
                disabled={loading}
              >
                <span className="material-symbols-outlined">arrow_back</span>
                Voltar
              </button>
              <button
                className="btn btn-primary"
                onClick={nextStep}
                disabled={loading || (currentStep === 1 && !formData.admin_name) || (currentStep === 2 && !formData.institution_type) || (currentStep === 4 && !formData.admin_phone) || (currentStep === 5 && !formData.church_name) || (currentStep === 6 && !formData.expected_members)}
              >
                {loading ? 'Processando...' : currentStep === totalSteps ? 'Finalizar' : 'Continuar'}
                {!loading && (
                  <span className="material-symbols-outlined">
                    {currentStep === totalSteps ? 'check' : 'arrow_forward'}
                  </span>
                )}
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default WelcomePage;
